import { Component, OnInit, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { PoiTypeSettingsService } from './poi-type-settings.service';
import { DefectAgendaService } from './defect-agenda.service';
import { MiddlewareApiService } from '../api/middleware-api.service';
import { TranslationService } from '../services/translation.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import {
  PoiTypeGroup,
  AvailableCulture,
  SitePerson,
  UserRole,
  MiddlewareWebSettings,
} from './settings.models';
import { getConfig } from '../config.service';

@Component({
  selector: 'app-settings-overview',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  template: `
    <div class="screen-modal-container">
      <a class="screen-close" (click)="close()"></a>

      <!-- Loading indicator -->
      <div class="settings-loading" *ngIf="isLoading">
        <div class="spinner"></div>
        <span>{{ 'loading' | translate:'Načítání' }}</span>
      </div>

      <div class="settings" *ngIf="!isLoading">
        <h1>{{ 'settings_view_header' | translate }}</h1>

        <ul>
          <!-- POI Type Groups -->
          <li *ngFor="let group of poiTypeGroups">
            <h2>{{ group.name }}</h2>
            <ul>
              <li *ngFor="let poiType of group.poiTypes">
                <input
                  type="checkbox"
                  [checked]="!isPoiTypeHidden(poiType.id)"
                  (click)="togglePoiType(poiType.id)"
                />
                <img [src]="getIconUrl(poiType.icon)" width="32" [alt]="poiType.name" />
                {{ poiType.name }}
              </li>
            </ul>
          </li>

          <!-- Language Section -->
          <li>
            <h2>{{ 'settings_language_label' | translate }}</h2>
            <ul>
              <li *ngFor="let lang of availableCultures">
                <input
                  type="radio"
                  name="language"
                  [value]="lang.code"
                  [checked]="currentLanguage === lang.code"
                  (click)="setLanguage(lang.code)"
                />
                {{ getLanguageName(lang.code) }}
              </li>
            </ul>
          </li>
        </ul>
      </div>

      <!-- Manager and Coordinator Settings (Admin only) -->
      <div class="manager-and-coordinator-settings" *ngIf="isAdmin && !isLoading">
        <label>Default Manager</label>
        <input
          type="text"
          readonly
          [value]="getPersonFullName(defaultManager)"
        />
        <a class="link-btn" (click)="setDefaultManager()">
          <img src="assets/images/edit_black.png" />
          <span>Change</span>
        </a>

        <label>Default Coordinator</label>
        <input
          type="text"
          readonly
          [value]="getPersonFullName(defaultCoordinator)"
        />
        <a class="link-btn" (click)="setDefaultCoordinator()">
          <img src="assets/images/edit_black.png" />
          <span>Change</span>
        </a>
      </div>
    </div>
  `,
  styles: [
    `
      .settings-loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 50px;
        color: #fff;
      }

      .spinner {
        width: 40px;
        height: 40px;
        border: 3px solid rgba(255, 255, 255, 0.3);
        border-radius: 50%;
        border-top-color: #fff;
        animation: spin 1s ease-in-out infinite;
        margin-bottom: 10px;
      }

      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }

      .settings {
        color: #fff;
        overflow-y: auto;

        h1 {
          font-size: 28px;
          margin-bottom: 20px;
        }

        > ul {
          padding: 0;
          display: flex;
          flex-wrap: wrap;
        }

        > ul > li {
          float: left;
          margin-right: 10px;
          padding: 5px;
          min-width: 300px;
          min-height: 220px;
          list-style: none;
        }

        > ul > li > ul {
          padding: 0;
        }

        h2 {
          font-size: 25px;
          color: #000;
          background-color: #fff;
          border-radius: 2px;
          padding: 5px;
        }

        ul li {
          list-style-type: none;
        }

        input[type='checkbox'] {
          width: 20px;
          display: inline-block;
          vertical-align: middle;
          cursor: pointer;
        }

        input[type='radio'] {
          width: 20px;
          display: inline-block;
          vertical-align: middle;
          cursor: pointer;
          margin-right: 5px;
        }

        img {
          vertical-align: middle;
          margin-right: 5px;
        }
      }

      .manager-and-coordinator-settings {
        margin-top: 30px;

        img {
          width: 18px;
          margin-right: 5px;
        }

        label {
          color: white;
          margin-right: 5px;
          display: block;
          margin-bottom: 5px;
        }

        input {
          border-radius: 5px;
          padding: 10px;
          margin-right: 5px;
          outline: none;
          border: 1px solid #ccc;
          min-width: 300px;
        }

        .link-btn {
          margin-right: 15px;
          cursor: pointer;
          color: #4ba82e;

          &:hover {
            text-decoration: underline;
          }
        }
      }
    `,
  ],
})
export class SettingsOverviewComponent implements OnInit {
  @Output() closeSettings = new EventEmitter<void>();

  private poiTypeSettingsService = inject(PoiTypeSettingsService);
  private defectAgendaService = inject(DefectAgendaService);
  private middlewareApi = inject(MiddlewareApiService);
  private oidcSecurityService = inject(OidcSecurityService);
  private translationService = inject(TranslationService);

  poiTypeGroups: PoiTypeGroup[] = [];
  availableCultures: AvailableCulture[] = [];
  currentLanguage = 'cs';
  isLoading = true;
  isAdmin = false;

  defaultManager: SitePerson | null = null;
  defaultCoordinator: SitePerson | null = null;

  private navvisApiUrl = '';
  private idToken = '';
  private outdoorDefectPoiTypeId?: number;

  // Default available languages (same as old application)
  private defaultCultures: AvailableCulture[] = [
    { code: 'cs', resxName: 'settings_language_cs' },
    { code: 'en', resxName: 'settings_language_en' },
    { code: 'de', resxName: 'settings_language_de' },
  ];

  ngOnInit(): void {
    // Initialize current language from TranslationService
    this.currentLanguage = this.translationService.getCurrentLanguage();
    this.loadSettings();
  }

  private async loadSettings(): Promise<void> {
    this.isLoading = true;
    this.navvisApiUrl = getConfig().api.navvisUrl.replace(/\/$/, '');

    // Set default languages (WebSettings endpoint has issues with session-based auth)
    this.availableCultures = this.defaultCultures;

    // Get ID token for image authentication
    this.oidcSecurityService.getIdToken().subscribe({
      next: (token) => {
        this.idToken = token ?? '';
      },
    });

    try {
      // First load middleware settings to get site-specific POI type IDs
      this.middlewareApi.getSettings().subscribe({
        next: async (settings) => {
          // Get site settings for current site
          const currentSiteId = (window as any).IvionApp?.currentSiteId;

          if (currentSiteId && settings.siteInfos) {
            const siteInfo = settings.siteInfos.find(
              (s) => s.navvisSiteId === currentSiteId
            );
            if (siteInfo?.settings) {
              this.defectAgendaService.setSiteSettings(siteInfo.settings);
              this.outdoorDefectPoiTypeId = siteInfo.settings.outdoorDefectPoiTypeId;
            }
          }

          // Now load POI type groups with outdoorDefectPoiTypeId for proper naming
          try {
            this.poiTypeGroups = await this.poiTypeSettingsService.loadPoiTypeGroups(
              this.currentLanguage,
              this.outdoorDefectPoiTypeId
            );
          } catch (error) {
            console.error('[SettingsOverview] Failed to load POI types:', error);
          }

          this.isLoading = false;
        },
        error: async (error) => {
          console.error('[SettingsOverview] Failed to load settings:', error);
          // Still try to load POI types even without middleware settings
          try {
            this.poiTypeGroups = await this.poiTypeSettingsService.loadPoiTypeGroups(this.currentLanguage);
          } catch (poiError) {
            console.error('[SettingsOverview] Failed to load POI types:', poiError);
          }
          this.isLoading = false;
        },
      });
    } catch (error) {
      console.error('[SettingsOverview] Failed to initialize settings:', error);
      this.isLoading = false;
    }
  }

  close(): void {
    this.closeSettings.emit();
  }

  isPoiTypeHidden(id: number): boolean {
    return this.poiTypeSettingsService.isPoiTypeHidden(id);
  }

  togglePoiType(id: number): void {
    this.poiTypeSettingsService.togglePoiType(id);

    // Check if this affects defect agenda
    if (this.defectAgendaService.isDefectPoiType(id)) {
      this.defectAgendaService.checkAndUpdateDefectAgenda();
    }
  }

  getIconUrl(icon: string): string {
    if (!icon) {
      return '';
    }

    // If icon already contains full URL, use it directly
    if (icon.startsWith('http://') || icon.startsWith('https://')) {
      return icon;
    }

    // Custom POI icons (starting with "poi/") need authentication via x_authorization query param
    if (icon.startsWith('poi')) {
      const authParam = this.idToken ? `?x_authorization=${encodeURIComponent(this.idToken)}` : '';
      return `${this.navvisApiUrl}/${icon}${authParam}`;
    }

    // Standard NavVis icons (img/poi_navvis/*) - no auth needed
    return `${this.navvisApiUrl}/${icon}`;
  }

  getLanguageName(code: string): string {
    const key = `settings_language_${code}`;
    const translated = this.translationService.translate(key);
    // Fall back to code if no translation found
    return translated !== key ? translated : code.toUpperCase();
  }

  async setLanguage(code: string): Promise<void> {
    this.currentLanguage = code;

    // Switch UI translations
    await this.translationService.switchLanguage(code);

    // Reload POI types with new language
    this.poiTypeGroups = await this.poiTypeSettingsService.loadPoiTypeGroups(
      code,
      this.outdoorDefectPoiTypeId
    );
  }

  getPersonFullName(person: SitePerson | null): string {
    if (!person) {
      return '';
    }
    return `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim();
  }

  setDefaultManager(): void {
    // TODO: Implement employee selector modal
    console.log('[SettingsOverview] setDefaultManager - not implemented yet');
  }

  setDefaultCoordinator(): void {
    // TODO: Implement employee selector modal
    console.log('[SettingsOverview] setDefaultCoordinator - not implemented yet');
  }
}
